import React, { useState } from "react";
import {
	Button,
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Label,
} from "reactstrap";
import Swal from "sweetalert2";
import { postponePayment } from "./AccountAPI";
import { useUserBhphPrivileges } from "../features/settings/subFeatures/settingsTabs/userManagement/useUserPrivileges";
import useCurrentAccountStore from "./AccountStore";
import shallow from "zustand/shallow";
import { SelectorElement } from "../features/forms/components/InputElement";
import dayjs from "dayjs";
import { useForm } from "react-hook-form";

const PostponeButton = () => {
	const { acct, amortizations } = useCurrentAccountStore(
		(state) => ({
			amortizations: state.amortizations,
			acct: state.account,
		}),
		shallow
	);

	const methods = useForm();
	const privileges = useUserBhphPrivileges();
	const p = privileges();

	const [showModal, setShowModal] = useState(false);
	const [selectedAmortId, setSelectedAmortId] = useState("");

	// Get list of non-deferred payments (any payment on or before next due date)
	const getPostponablePayments = () => {
		const nextTs = acct.nextDueDate
			? new Date(acct.nextDueDate).getTime()
			: null;

		// Check if this is a lease/RTO account
		const isLeaseOrRTO = acct.payoffMethod === 4 || acct.payoffMethod === 5;

		return amortizations
			.filter((amort) => {
				// Skip if already deferred
				if (amort.isDeferred) return false;

				// For Lease/RTO: skip first payment (pmtNo 1) as it may be capitalized in drive-off
				if (isLeaseOrRTO && amort.pmtNo === 1) return false;

				// Include any scheduled payment on or before next due date (including fully paid)
				const dueTs = amort.dueDate ? new Date(amort.dueDate).getTime() : null;
				return dueTs !== null && nextTs !== null && dueTs <= nextTs;
			})
			.map((amort) => ({
				label: `Payment #${amort.pmtNo} - Due ${dayjs(amort.dueDate).format(
					"MM/DD/YYYY"
				)}`,
				value: amort.id,
				dueDate: amort.dueDate,
			}));
	};

	const postponablePayments = getPostponablePayments();

	const handleOpenModal = () => {
		if (postponablePayments.length === 0) {
			Swal.fire(
				"No Payments",
				"There are no payments available to postpone.",
				"info"
			);
			return;
		}

		// Set default to payment with next due date
		const nextDuePayment = postponablePayments.find((p) =>
			dayjs(p.dueDate).format("YYYY-MM-DD") === dayjs(acct.nextDueDate).format("YYYY-MM-DD")
		);

		if (nextDuePayment) {
			setSelectedAmortId(nextDuePayment.value);
		}

		setShowModal(true);
	};

	const handleConfirmPostpone = () => {
		if (!selectedAmortId) {
			Swal.fire("Error!", "Please select a payment to postpone.", "error");
			return;
		}

		postponePayment(selectedAmortId).then(
			() => {
				Swal.fire(
					"Postponed!",
					"Payment has been postponed successfully.",
					"success"
				);
				setShowModal(false);
				setSelectedAmortId("");
				window.location.reload();
			},
			(error) => {
				Swal.fire("Error!", "Failed to postpone payment.", "error");
			}
		);
	};

	const handlePaymentSelect = (e) => {
		const amortId = e.target.value;
		setSelectedAmortId(amortId);
	};

	return (
		<>
			<Button
				color="warning"
				disabled={!p.postponePayment}
				onClick={handleOpenModal}
			>
				Postpone Payment
			</Button>

			<Modal isOpen={showModal} toggle={() => setShowModal(false)}>
				<ModalHeader toggle={() => setShowModal(false)}>
					Postpone Payment
				</ModalHeader>
				<ModalBody>
					<SelectorElement
						{...methods}
						name="selectedPayment"
						label="Select Payment to Postpone"
						value={selectedAmortId}
						options={postponablePayments}
						onChange={handlePaymentSelect}
						colSize="col-sm-8"
						disableValidation
					/>
				</ModalBody>
				<ModalFooter>
					<Button color="secondary" onClick={() => setShowModal(false)}>
						Cancel
					</Button>
					<Button color="warning" onClick={handleConfirmPostpone}>
						Confirm Postpone
					</Button>
				</ModalFooter>
			</Modal>
		</>
	);
};

export default PostponeButton;
